import { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Mic, Camera, Image, Send, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { withTimeout } from '@/lib/with-timeout';
import { useRole } from '@/contexts/RoleContext';
import { handleSupabaseError } from '@/lib/supabase-error-handler';
import { compressImageSafeCanvas } from '@/lib/image-compression';
import { cn } from '@/lib/utils';
import { ADMIN_OPEN_ISSUES_QUERY_KEY } from '@/lib/adminOpenIssues';
import { CLEANER_PROPERTIES_QUERY_KEY } from '@/lib/cleanerQueryKeys';
import { insertCleaningPropertyIssue } from '@/lib/cleaningIssueApi';
import { enqueueIssue } from '@/lib/offline/offlineQueue';
import { putPhotoBlob } from '@/lib/offline/offlineDb';
import { isNetworkFailure } from '@/lib/offline/offlineTypes';

const ISSUE_BUCKET = 'property-issues';
const TEMP_UPLOADS_PREFIX = 'temp_uploads';

interface IssueReportSectionProps {
  disabled?: boolean;
  locationId: string;
  sectionId?: string;
  sectionName?: string;
}

interface PendingIssue {
  description: string;
  locationId: string;
  orgId: string;
  reporterId: string;
  sectionName?: string;
  photoUrl?: string;
  createdAt: number;
}

/**
 * Upload flow aligned with Domio-Serwis PublicIssueReport / storage upload:
 * direct supabase.storage.from(...).upload(...) + getPublicUrl — no Promise.race / watchdogs.
 */
export const IssueReportSection = ({ 
  disabled = false, 
  locationId,
  sectionId,
  sectionName
}: IssueReportSectionProps) => {
  const [issueDescription, setIssueDescription] = useState('');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const { currentOrgId } = useRole();
  const queryClient = useQueryClient();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const baseTextRef = useRef<string>('');

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition ?? window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'pl-PL';

        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          // Use base text from start of recording + new transcript to avoid duplicates
          const baseText = baseTextRef.current.trim();
          const fullTranscript = finalTranscript + interimTranscript;
          const newText = baseText ? `${baseText} ${fullTranscript}`.trim() : fullTranscript.trim();
          
          setIssueDescription(newText);
        };

        recognition.onerror = (event: any) => {
          console.error('[IssueReportSection] Speech recognition error:', event.error);
          setIsRecording(false);
          
          if (event.error === 'no-speech') {
            toast.info('Nie wykryto mowy. Spróbuj ponownie.');
          } else if (event.error === 'audio-capture') {
            toast.error('Nie można uzyskać dostępu do mikrofonu.');
          } else if (event.error === 'not-allowed') {
            toast.error('Brak uprawnień do mikrofonu.');
          }
        };

        recognition.onend = () => {
          setIsRecording(false);
        };

        recognitionRef.current = recognition;
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  // Cleanup legacy payload fields used by previous Base64 offline photo queue.
  useEffect(() => {
    try {
      const pendingIssuesStr = localStorage.getItem('pending_issues');
      if (!pendingIssuesStr) return;
      const pendingIssues: PendingIssue[] = JSON.parse(pendingIssuesStr);
      const sanitizedIssues = pendingIssues.map((issue) => ({
        description: issue.description,
        locationId: issue.locationId,
        orgId: issue.orgId,
        reporterId: issue.reporterId,
        sectionName: issue.sectionName,
        photoUrl: issue.photoUrl,
        createdAt: issue.createdAt,
      }));
      localStorage.setItem('pending_issues', JSON.stringify(sanitizedIssues));
    } catch (error) {
      console.warn('[IssueReportSection] Nie udało się wyczyścić legacy danych offline:', error);
    }
  }, []);

  // Sync offline issues when connection is restored
  useEffect(() => {
    const syncOfflineIssues = async () => {
      try {
        const pendingIssuesStr = localStorage.getItem('pending_issues');
        if (!pendingIssuesStr) return;

        const pendingIssues: PendingIssue[] = JSON.parse(pendingIssuesStr);
        if (pendingIssues.length === 0) return;

        console.log(`[IssueReportSection] Synchronizacja ${pendingIssues.length} zgłoszeń offline...`);

        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          console.error('[IssueReportSection] Brak użytkownika podczas synchronizacji offline');
          return;
        }

        const syncedIssues: number[] = [];
        const failedIssues: PendingIssue[] = [];

        // Process each pending issue
        for (let i = 0; i < pendingIssues.length; i++) {
          const pendingIssue = pendingIssues[i];
          try {
            let photoUrl: string | null = null;

            if (pendingIssue.photoUrl && pendingIssue.photoUrl.startsWith('http')) {
              photoUrl = pendingIssue.photoUrl;
            }

            // Get auto_notify_issues flag for this location
            const { data: locationData } = await supabase
              .from('cleaning_locations')
              .select('auto_notify_issues')
              .eq('id', pendingIssue.locationId)
              .maybeSingle();

            const autoNotifyIssues = locationData?.auto_notify_issues === true;

            await insertCleaningPropertyIssue({
              description: pendingIssue.description,
              locationId: pendingIssue.locationId,
              orgId: pendingIssue.orgId,
              reporterId: pendingIssue.reporterId,
              photoUrl,
              autoNotify: autoNotifyIssues,
            });
            syncedIssues.push(i);
          } catch (error) {
            console.error(`[IssueReportSection] Błąd podczas synchronizacji zgłoszenia offline ${i}:`, error);
            failedIssues.push(pendingIssue);
          }
        }

        // Remove synced issues from localStorage
        if (syncedIssues.length > 0) {
          const remainingIssues = pendingIssues.filter((_, index) => !syncedIssues.includes(index));
          
          if (remainingIssues.length > 0) {
            localStorage.setItem('pending_issues', JSON.stringify(remainingIssues));
          } else {
            localStorage.removeItem('pending_issues');
          }

          toast.success(`Zsynchronizowano ${syncedIssues.length} zgłoszeń offline`);
          console.log(`[IssueReportSection] Zsynchronizowano ${syncedIssues.length} zgłoszeń offline`);
          void queryClient.invalidateQueries({ queryKey: [ADMIN_OPEN_ISSUES_QUERY_KEY] });
          void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });
        }

        // Keep failed issues for retry
        if (failedIssues.length > 0) {
          localStorage.setItem('pending_issues', JSON.stringify(failedIssues));
          console.warn(`[IssueReportSection] Nie udało się zsynchronizować ${failedIssues.length} zgłoszeń offline`);
        }
      } catch (error) {
        console.error('[IssueReportSection] Błąd podczas synchronizacji zgłoszeń offline:', error);
      }
    };

    // Listen for online event
    const handleOnline = () => {
      console.log('[IssueReportSection] Połączenie przywrócone, rozpoczynam synchronizację...');
      syncOfflineIssues();
    };

    window.addEventListener('online', handleOnline);

    // Also try to sync on mount if online
    if (navigator.onLine) {
      syncOfflineIssues();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [queryClient]);

  const handleMicClick = () => {
    if (disabled) return;

    if (!recognitionRef.current) {
      toast.error('Rozpoznawanie mowy nie jest dostępne w tej przeglądarce.');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        // Save current description as base text at the start of recording
        baseTextRef.current = issueDescription;
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('[IssueReportSection] Error starting recognition:', error);
        toast.error('Nie można uruchomić rozpoznawania mowy.');
      }
    }
  };

  const handleCameraClick = () => {
    if (disabled) return;
    cameraInputRef.current?.click();
  };

  const handleGalleryClick = () => {
    if (disabled) return;
    galleryInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik graficzny');
      return;
    }

    let compressedFile: File;
    try {
      compressedFile = await compressImageSafeCanvas(file);
    } catch (err) {
      console.error('[IssueReportSection] Błąd kompresji:', err);
      toast.error('Nie udało się przetworzyć zdjęcia');
      return;
    }

    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }

    const localPreviewUrl = URL.createObjectURL(compressedFile);
    setPhotoPreview(localPreviewUrl);
    setSelectedPhoto(compressedFile);

    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const handleSubmitIssue = async () => {
    if (disabled || isSubmitting) return;
    if (!issueDescription.trim()) {
      toast.error('Wprowadź opis usterki');
      return;
    }

    if (!currentOrgId) {
      toast.error('Brak organizacji. Zaloguj się ponownie.');
      return;
    }

    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }

    let finalPhotoUrl: string | null = null;

    setIsSubmitting(true);

    try {
      const { data: { session } } = await withTimeout(supabase.auth.getSession(), 8000);
      const user = session?.user;

      if (!user) {
        toast.error('Brak zalogowanego użytkownika. Zaloguj się ponownie.');
        return;
      }

      const descriptionWithPrefix = sectionName
        ? `[${sectionName}] - ${issueDescription.trim()}`
        : issueDescription.trim();

      if (!navigator.onLine) {
        let photoBlobId: string | undefined;
        if (selectedPhoto) {
          photoBlobId = crypto.randomUUID();
          await putPhotoBlob(photoBlobId, selectedPhoto);
        }
        await enqueueIssue({
          description: descriptionWithPrefix,
          locationId,
          orgId: currentOrgId,
          reporterId: user.id,
          sectionName,
        }, photoBlobId);
        toast.info('Brak internetu. Zgłoszenie i zdjęcie zapisane — wyślemy po odzyskaniu sieci.');
        setIssueDescription('');
        setPhotoPreview(null);
        setSelectedPhoto(null);
        if (cameraInputRef.current) cameraInputRef.current.value = '';
        if (galleryInputRef.current) galleryInputRef.current.value = '';
        return;
      }

      // Deferred upload: wyślij zdjęcie dopiero przy submit.
      if (selectedPhoto) {
        try {
          if (!(selectedPhoto instanceof File)) {
            throw new Error('Nieprawidłowy plik zdjęcia do wysyłki');
          }

          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(2, 9);
          const filePath = `${TEMP_UPLOADS_PREFIX}/${locationId}/${timestamp}-${randomId}.jpg`;

          const { error: uploadError } = await supabase.storage
            .from(ISSUE_BUCKET)
            .upload(filePath, selectedPhoto, {
              cacheControl: '3600',
              upsert: true,
            });
          if (uploadError) {
            console.error('[IssueReportSection] Storage upload:', uploadError);
            throw new Error(uploadError.message || 'Błąd wgrywania zdjęcia');
          }

          const {
            data: { publicUrl },
          } = supabase.storage.from(ISSUE_BUCKET).getPublicUrl(filePath);
          finalPhotoUrl = publicUrl || null;
        } catch (fallbackError) {
          console.error('[Submit] Upload error:', fallbackError);
          const uploadErrorMessage = fallbackError instanceof Error
            ? fallbackError.message
            : 'Błąd połączenia. Nie udało się przesłać zdjęcia.';
          toast.error(uploadErrorMessage);
          return;
        }
      }

      const { data: locationData } = await supabase
        .from('cleaning_locations')
        .select('auto_notify_issues')
        .eq('id', locationId)
        .maybeSingle();

      const autoNotifyIssues = locationData?.auto_notify_issues === true;

      try {
        await insertCleaningPropertyIssue({
          description: descriptionWithPrefix,
          locationId,
          orgId: currentOrgId,
          reporterId: user.id,
          photoUrl: finalPhotoUrl,
          autoNotify: autoNotifyIssues,
        });
      } catch (insertError) {
        handleSupabaseError(insertError, 'IssueReportSection');
        return;
      }

      toast.success('Usterka została zgłoszona');
      void queryClient.invalidateQueries({ queryKey: [ADMIN_OPEN_ISSUES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });
      setIssueDescription('');
      setPhotoPreview(null);
      setSelectedPhoto(null);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    } catch (err) {
      if (isNetworkFailure(err) && currentOrgId) {
        try {
          const { data: { session } } = await withTimeout(supabase.auth.getSession(), 8000);
          const userId = session?.user?.id;
          if (userId) {
            let photoBlobId: string | undefined;
            if (selectedPhoto) {
              photoBlobId = crypto.randomUUID();
              await putPhotoBlob(photoBlobId, selectedPhoto);
            }
            const description = sectionName
              ? `[${sectionName}] - ${issueDescription.trim()}`
              : issueDescription.trim();
            await enqueueIssue({
              description,
              locationId,
              orgId: currentOrgId,
              reporterId: userId,
              sectionName,
            }, photoBlobId);
            toast.info('Brak internetu. Zgłoszenie zapisane offline.');
            setIssueDescription('');
            setPhotoPreview(null);
            setSelectedPhoto(null);
            return;
          }
        } catch (queueError) {
          console.error('[IssueReportSection] Offline queue error:', queueError);
        }
      }
      const errorMessage = err instanceof Error ? err.message : 'Nieznany błąd';
      console.error('[IssueReportSection] Nieoczekiwany błąd:', err);
      toast.error(`Wystąpił błąd: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Issue description */}
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          Opis usterki
        </label>
        <div className="relative">
          <Textarea
            value={issueDescription}
            onChange={(e) => setIssueDescription(e.target.value)}
            placeholder="Opisz usterkę lub naciśnij ikonę mikrofonu..."
            className="min-h-[100px] pr-12 resize-none bg-white dark:bg-zinc-900"
            disabled={disabled}
          />
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              "absolute right-2 top-2 h-8 w-8 text-muted-foreground hover:text-primary",
              isRecording && "text-red-500 hover:text-red-600"
            )}
            disabled={disabled}
            onClick={handleMicClick}
            type="button"
          >
            <Mic className={cn("h-5 w-5", isRecording && "animate-pulse")} />
          </Button>
        </div>
        {isRecording && (
          <p className="text-xs text-red-600 dark:text-red-400 font-medium">
            ● Nagrywanie... Mów teraz
          </p>
        )}
        {!isRecording && (
          <p className="text-xs text-muted-foreground">
            Kliknij ikonę mikrofonu, aby dyktować usterkę
          </p>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        id="issue-photo-camera-input"
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled}
      />
      <input
        ref={galleryInputRef}
        id="issue-photo-gallery-input"
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled}
      />

      {/* Photo buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1 h-11 justify-center"
          onClick={handleCameraClick}
        disabled={disabled}
        >
          <Camera className="mr-2 h-4 w-4" />
          Zrób zdjęcie
        </Button>
        <Button
          variant="outline"
          className="flex-1 h-11 justify-center"
          onClick={handleGalleryClick}
        disabled={disabled}
        >
          <Image className="mr-2 h-4 w-4" />
          Dodaj z galerii
        </Button>
      </div>
      
      {/* Photo preview */}
      {photoPreview && (
        <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <img
            src={photoPreview}
            alt="Podgląd zdjęcia usterki"
            className="w-full h-48 object-cover"
          />
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8"
            onClick={() => {
              if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
              }
              setPhotoPreview(null);
              setSelectedPhoto(null);
              if (cameraInputRef.current) cameraInputRef.current.value = '';
              if (galleryInputRef.current) galleryInputRef.current.value = '';
            }}
            disabled={disabled || isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      <p className="text-xs text-muted-foreground">
        Możesz zrobić zdjęcie lub wybrać z galerii
      </p>

      {/* Submit button */}
      <Button 
        className="w-full h-11 font-medium bg-amber-500 hover:bg-amber-600 text-white"
        onClick={handleSubmitIssue}
        disabled={!issueDescription.trim() || disabled || isSubmitting}
      >
        {isSubmitting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Send className="mr-2 h-4 w-4" />
        )}
        {isSubmitting ? 'Zapisywanie...' : 'Wyślij zgłoszenie'}
      </Button>
    </div>
  );
};
